import { Component, OnInit, Input, OnDestroy, Output, EventEmitter, ChangeDetectorRef, Inject, OnChanges, SimpleChanges } from '@angular/core';
import { Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { registerLocaleData } from '@angular/common';
import de from '@angular/common/locales/de';

import { VersionStatus, Credits, Versions, Projects, ProjectStatus, Supportrequestsinfo, Env } from '@usimweb/core/models';

import { Card } from './card';
import { AuthHandler } from '@usimweb/core-auth';

@Component({
    selector: 'usimweb-projectcard',
    // changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './projectcard.component.html',
    styleUrls: ['./projectcard.component.scss'],
    standalone: false
})
export class ProjectcardComponent extends Card implements OnInit, OnDestroy, OnChanges {
  @Input() isProjectCard = false;

  @Input() isGeometry = false;

  @Input() newCardTitle = '';

  @Input() link: string = '';

  @Input() newItemLink: string = '';

  @Input() emptyProjectLink: string = '';

  @Input() newProject?: boolean;

  @Input() cardItem!: Projects | Versions;

  @Input() loading: boolean = true;

  @Input() showBar: boolean = false;

  @Input() compare: boolean = false;

  @Input() isMeshing: boolean = false;

  @Input() isEditor: boolean = false;

  @Input() user: string = '';

  @Output() favouriteChangedEvent: EventEmitter<any> = new EventEmitter<any>();

  @Output() selectedIndexEvent: EventEmitter<any> = new EventEmitter<any>();

  @Output() dialogEvent: EventEmitter<any> = new EventEmitter<any>();

  @Output() deleteEvent: EventEmitter<Projects | Versions> = new EventEmitter<Projects | Versions>();

  @Output() renameSimEvent: EventEmitter<Versions> = new EventEmitter<Versions>();

  @Output() routeEvent: EventEmitter<string> = new EventEmitter<string>();

  @Output() openDialogEvent: EventEmitter<any> = new EventEmitter<any>();

  @Output() selectEmitter: EventEmitter<any> = new EventEmitter<any>();

  constructor(@Inject('environment') environment: Env, private router: Router, private snackBar: MatSnackBar, private cd: ChangeDetectorRef) {
    super(environment);
  }

  ngOnInit(): void {
    registerLocaleData(de);

    if (this.cardItem) {
      this.createCard();

      if (this.isMeshing) {
        this.createMeshingCard();
        return;
      }

      if (this.isProjectCard) {
        this.createProjectCard();
        return;
      }

      this.createVersionCard();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    const cardItem = this.cardItem as Projects;

    if (cardItem && cardItem.support_requests_info && changes['cardItem']) {
      if (cardItem.support_requests_info.length > 0) {
        this.isSupport = true;
      } else {
        this.isSupport = false;
      }
    }

    if (cardItem && cardItem.created_by.toLowerCase() !== AuthHandler.basfUserName.toLowerCase()) {
      this.isSupport = true;
    }
  }

  ngOnDestroy() {
    this.isTaskAlive = false;
  }

  private createCard() {
    this.newCardTitle = 'New project';
    const cardItem = this.cardItem as Projects;

    this.projectName = cardItem.name;
    this.created_at = cardItem.created_at;
    this.created_by = cardItem.created_by;

    this.materialName = cardItem.description !== undefined ? cardItem.description : 'No description';

    this.loading = false;
    this.preview = false;
  }

  private createMeshingCard() {
    const cardItem = this.cardItem as Versions;
    this.isSupport = false;

    this.previewMargin = '50px';
    this.cardHeight = '300px';

    if (typeof this.cardItem.credits === 'number') {
      this.credits = Math.round(this.cardItem.credits) || 0;
    }

    this.projectName = cardItem?.appType || cardItem?.name || cardItem?.title || '';
    this.uuid = cardItem?.uuid || '';
    this.progress = cardItem?.progress || 0;

    if (cardItem.png.includes('png')) {
      this.preview = cardItem.png;
    }
    if (cardItem.modelname) {
      this.modelname = cardItem.modelname;
    }

    this.menuItems = [{ icon: 'delete', text: 'Delete', item: 'delete_geometry', toolTip: 'Delete geometry...', type: 'button' }];
  }

  private createVersionCard() {
    this.newCardTitle = 'New simulation';
    const cardItem = this.cardItem as Versions;

    this.noImageMargin = '35px';
    this.uuid = cardItem?.uuid || '';
    this.progress = cardItem.progress || 0;

    if (typeof cardItem.credits === 'number') {
      this.credits = Math.round(cardItem.credits) || 0;
    }

    this.owner = cardItem?.created_by || '';
    this.versionStatus = cardItem?.status;
    this.projectName = cardItem.title || cardItem.name || '';

    if (this.newProject) {
      return;
    }

    if (cardItem.vela) {
      this.heatLossW = cardItem.vela.heatLossW;
      this.volume = cardItem.vela.volume;
      const label = cardItem.vela.energyLabel;
      if (label) {
        this.updateCardProjectVersionsPreviews(label);
      }
      return;
    }

    if (cardItem.png && cardItem.png.includes('png')) {
      this.preview = cardItem.png;
    }
  }

  private createProjectCard() {
    //document.documentElement.style.setProperty('--card-background', '#f0f0f0');
    const cardItem = this.cardItem as Projects;

    this.project = cardItem;

    this.progress = 100;
    this.previewMargin = '50px';
    this.descriptionMargin = '32px';
    this.multipleStatusBar = true;

    if (cardItem.credits) {
      const credits_ = cardItem.credits as Credits;
      this.credits = Math.round(credits_.simulation || 0);
    }

    //FIXME already set in createCard
    this.projectName = this.cardItem.title !== undefined ? this.cardItem.title : this.cardItem.name;

    if (cardItem?.description) {
      this.materialName = cardItem?.description;
    } else {
      this.materialName = 'No description';
    }

    this.created_at = cardItem?.created_at || '';
    this.owner = cardItem?.created_by || '';

    const versionOne = cardItem.versions[0];
    const status = cardItem.status as ProjectStatus;
    if (versionOne && versionOne.progress === 100) {
      this.projectStatus = { draft: 0, failed: 0, success: 1, processing: 0, waiting: 0 };
    } else {
      this.projectStatus = status || this.projectStatus;
    }

    this.id = (cardItem?.uuid || '0').toString();

    const support = cardItem.support_requests_info;
    if (support) {
      this.isSupport = this.isProjectSupported(support);
    }

    if (cardItem.versions) {
      this.isSim = this.checkIsSim(cardItem.versions);
    }

    if (cardItem.energyLabel) {
      this.heatLossW = cardItem.heatLossW;
      this.volume = cardItem.volume;
      const label = cardItem.energyLabel;
      this.updateCardProjectVersionsPreviews(label);
      return;
    }

    if (cardItem.png && cardItem.png.includes('png')) {
      this.preview = cardItem.png;
    }

    if (cardItem.config) {
      this.stonChipConfig = cardItem.config;
      this.preview = true;
      this.materialName = 'Angle: ' + this.stonChipConfig.loadcase.angle + '°' + ' Velocity ' + this.stonChipConfig.loadcase.vel + ' km/h';
    }
  }

  private updateCardProjectVersionsPreviews(label: string) {
    this.preview = 'assets/labels/' + label + '.png';
    this.loading = false;

    this.cd.detectChanges();
  }

  private isProjectSupported(support: Supportrequestsinfo[]) {
    for (const supportRequest of support) {
      const allowStatuses = ['new_request', 'in_progress'];
      if (allowStatuses.includes(supportRequest.status)) {
        return true;
      }
    }

    if (this.cardItem && this.cardItem.created_by.toLowerCase() !== AuthHandler.basfUserName.toLowerCase()) {
      return true;
    }

    return false;
  }

  public route() {
    this.selectedIndexEvent.emit(this.cardItem.index);

    // if compare active
    // disable routing, selection only active
    if (this.compareActive) {
      this.selected();
    } else {
      // if meshing card
      if (this.isMeshing) {
        this.selectEmitter.emit(this.cardItem);
      } else {
        if (this.isProjectCard) {
          // if projects has versions
          // go to versions
          if (this.isSim) {
            if (this.environment.projectLink === 'viewer') {
              const versions = (this.cardItem as Projects).versions;
              const index = versions.length > 0 ? versions[0].version || versions.length : versions.length;
              this.router.navigate(['app/' + this.link + '/' + this.id + '/' + index]);
            } else {
              this.router.navigate(['app/' + this.link + '/' + this.id]);
            }
            // if project is empty
          } else {
            //this.isEditor = this.checkPermission(this.cardItem);
            // if(this.isEditor) {
            //   this.router.navigate(['app/newFoaming/' + this.id]);
            // } else {
            //   this.snackBar.open('There are no simulations in this project...' , 'OK' , {duration: 2000});
            // }
            this.router.navigate(['app/' + this.emptyProjectLink + '/' + this.id]);
          }
        }
        // if version card
        else {
          const project = (this.cardItem as Versions)?.project;
          const project_uuid = project;
          this.router.navigate(['app/' + this.link + '/' + project_uuid + '/' + this.uuid]);
        }
      }
    }
  }

  public selected() {
    this.isCompareSelected = !this.isCompareSelected;
  }

  public openDialog(event: any) {
    this.dialogEvent.emit(event);
  }

  public favouriteChanged(event: boolean) {
    this.favouriteChangedEvent.emit(event);
  }

  //FIX on update foamingStatus (process, finished etc..)
  public updateStatus(progress: number, status: VersionStatus, estimatedTime: number) {
    this.progress = progress;
    this.versionStatus = status;
    this.estimatedTime = estimatedTime + ' h';
    this.versionStatus = status;

    this.cd.detectChanges();
  }

  public updateSupported(obj: any) {
    this.isSupport = obj.isSupport;
    this.cardItem = obj.project;
  }

  public menuItemClicked(event = { item: '', uuid: '' }) {
    this.dialogEvent.emit({ event: event.item, uuid: this.uuid, data: this.cardItem });
    //this.dialogEvent.emit(event);
  }

  // if new projectcard item click
  public newItemClicked(link: string) {
    if (this.isGeometry) {
      this.openDialogEvent.emit();
      return;
    }
    if (this.isMeshing) {
      this.openDialogEvent.emit();
      return;
    }

    if (this.isFoaming && !this.isEditor) {
      this.snackBar.open('Please contact the project owner, to get "editor" permission...', 'OK', { duration: 5000 });
      return;
    }

    this.router.navigate(['app/' + link]);
  }

  public updateView() {
    this.cd.detach();
  }
}
